import { useState } from 'react';

import { useGetApplication } from '@api/lendingAPI/lapiHooks';
import { getAccountsQueryConfig } from '@api/lendingAPI/queryConfigs';
import { FormattedCurrency } from '@iwIntlProvider/FormattedCurrency';
import { IconArrowLeft } from '@iwoca/icons';
import { Button } from '@iwoca/orion';
import { useQuery } from '@tanstack/react-query';
import { FormattedDate, FormattedMessage } from 'react-intl';

import TopUpStatusMessage from './TopUpStatusMessage';
import { useStateKey } from '../../../../../iwQueryClient/useStateKey';
import DrawdownPanel from '../../DrawdownPanel/DrawdownPanel';
import { TopUpForm } from '../../TopUpForm/TopUpForm';
import styles from '../RequestMoreFundsPanel.module.css';

type TOptions = 'none_selected' | 'drawdown' | 'request_more_funds';

export const PreApproved = ({ accountId }: { accountId: string }) => {
    const stateKey = useStateKey();
    const {
        data: accountsResponse,
        isLoading: accountsLoading,
        isError: accountsError
    } = useQuery(getAccountsQueryConfig(stateKey));
    const [selectedOption, setSelectedOption] =
        useState<TOptions>('none_selected');

    const {
        data: applicationResponse,
        isLoading: applicationLoading,
        isError: applicationError
    } = useGetApplication(stateKey);

    if (
        accountsLoading ||
        applicationLoading ||
        accountsError ||
        applicationError
    )
        return null;

    const account = accountsResponse.data!.accounts.find(
        (account) => account.account_id === accountId
    )!;
    const approvalAmount = account.latest_formal_offer?.available_cash;
    const expiryDate = account.latest_formal_offer?.timeout;

    const applicationDeclined = applicationResponse!.status === 'declined';

    switch (selectedOption) {
        case 'drawdown':
            return (
                <>
                    <BackButton setSelectedOption={setSelectedOption} />
                    <DrawdownPanel accountId={accountId} />
                </>
            );
        case 'request_more_funds':
            return (
                <>
                    {!applicationDeclined && (
                        <BackButton setSelectedOption={setSelectedOption} />
                    )}
                    <TopUpForm
                        accountId={accountId}
                        formHeader={
                            <div className={styles.formHeader}>
                                <h2 className={styles.heading}>
                                    <FormattedMessage
                                        id="_.outstanding_view.preApprovedMoreFundsCTA"
                                        defaultMessage="Request more funds"
                                    />
                                </h2>
                                <p className={styles.description}>
                                    <FormattedMessage
                                        id="_.outstanding_view.requestMoreFundsHeading"
                                        defaultMessage="You're already approved for {amount}. Approval to more funds is subject to credit review."
                                        values={{
                                            amount: (
                                                <strong>
                                                    <FormattedCurrency
                                                        value={approvalAmount!}
                                                    />
                                                </strong>
                                            )
                                        }}
                                    />
                                </p>
                            </div>
                        }
                    />
                </>
            );
        default:
            return (
                <>
                    <TopUpStatusMessage
                        heading={
                            <FormattedMessage
                                id="_.outstanding_view.preApprovedHeading"
                                defaultMessage="You've been automatically approved"
                            />
                        }
                        description={
                            <FormattedMessage
                                id="_.outstanding_view.preApprovedDescription"
                                defaultMessage="You can drawdown up to {amount} before {date}"
                                values={{
                                    amount: (
                                        <strong>
                                            <FormattedCurrency
                                                value={approvalAmount!}
                                            />
                                        </strong>
                                    ),
                                    date: (
                                        <strong>
                                            <FormattedDate
                                                value={new Date(expiryDate!)}
                                                day="2-digit"
                                                month="short"
                                                year="numeric"
                                            />
                                        </strong>
                                    )
                                }}
                            />
                        }
                    />
                    <div className={styles.extraContentContainer}>
                        <Button
                            variant="success"
                            className={styles.cta}
                            onClick={() => setSelectedOption('drawdown')}
                        >
                            <FormattedMessage
                                id="_.outstanding_view.preApprovedCTA"
                                defaultMessage="Choose how much"
                            />
                        </Button>
                        {!applicationDeclined && (
                            <div className={styles.extraContent}>
                                <p className={styles.extraContentDescription}>
                                    <FormattedMessage
                                        id="_.outstanding_view.preApprovedMoreFunds"
                                        defaultMessage="Need more than {amount}?"
                                        values={{
                                            amount: (
                                                <strong>
                                                    <FormattedCurrency
                                                        value={approvalAmount!}
                                                    />
                                                </strong>
                                            )
                                        }}
                                    />
                                </p>
                                <Button
                                    variant="tertiary"
                                    onClick={() =>
                                        setSelectedOption('request_more_funds')
                                    }
                                >
                                    <FormattedMessage
                                        id="_.outstanding_view.preApprovedMoreFundsCTA"
                                        defaultMessage="Request more funds"
                                    />
                                </Button>
                            </div>
                        )}
                    </div>
                </>
            );
    }
};

const BackButton = ({
    setSelectedOption
}: {
    setSelectedOption: React.Dispatch<React.SetStateAction<TOptions>>;
}) => {
    return (
        <Button
            variant="tertiary"
            onClick={() => setSelectedOption('none_selected')}
            className={styles.backButton}
        >
            <IconArrowLeft />
            <FormattedMessage
                id="_.outstanding_view.backButtonCTA"
                defaultMessage="Back"
            />
        </Button>
    );
};
